import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Lock, Unlock } from "lucide-react";
import DateField from "@/components/DateField";
import { todayLocal } from "@/lib/timefmt";
import { STATUS_META } from "@/lib/taskStatus";

const inputCls = "w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm";
const labelCls = "block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5";

export default function PostTaskPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const taskLabel = user?.terminology?.task_label || "Task";
  const todayStr = todayLocal();
  const privileged = (user?.privilege || "user") !== "user" || user?.role === "admin" || !!user?.admin_level;

  const [date, setDate] = useState(todayStr);
  const [tasks, setTasks] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [selId, setSelId] = useState(params.get("task") || "");
  const [cfg, setCfg] = useState({ modes: [], sequences: [] });
  const [locs, setLocs] = useState([]);
  const [editDays, setEditDays] = useState(2);
  const [form, setForm] = useState(null);
  const [perfModes, setPerfModes] = useState([]);
  const [perfSeqs, setPerfSeqs] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [busy, setBusy] = useState(false);

  const taskLocs = locs.filter((l) => l.task_selectable !== false);

  const load = () => {
    if (!user?.company_id) return;
    const from = new Date(`${date}T00:00:00`);
    from.setDate(from.getDate() - 31); // include multi-day tasks that started earlier but span this date
    const p2 = (x) => String(x).padStart(2, "0");
    const fromStr = `${from.getFullYear()}-${p2(from.getMonth() + 1)}-${p2(from.getDate())}`;
    const covering = (list) => list.filter((t) => t.date <= date && (t.end_date || t.date) >= date);
    api.get(`/org/tasks?company_id=${user.company_id}&from_date=${fromStr}&to_date=${date}`)
      .then((r) => setTasks(covering(r.data)))
      .catch(() => api.get(`/tasks?from_date=${fromStr}&to_date=${date}`).then((r) => setTasks(covering(r.data))).catch(() => {}));
  };
  useEffect(load, [date, user?.company_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/task-config?company_id=${user.company_id}`).then((r) => setCfg(r.data)).catch(() => {});
    api.get(`/locations?company_id=${user.company_id}`).then((r) => setLocs(r.data)).catch(() => {});
    api.get(`/companies/${user.company_id}/task-settings`).then((r) => setEditDays(r.data.post_task_edit_days ?? 14)).catch(() => {});
  }, [user?.company_id]);

  const eligible = useMemo(() => tasks.filter((t) => {
    if ((t.end_date || t.date) > todayStr) return false;
    if (t.status === "completed") return showCompleted;
    if (t.requires_authorisation) return t.status === "authorised";
    return t.status === "committed";
  }), [tasks, todayStr, showCompleted]);
  const sel = tasks.find((t) => t.id === selId) || null;

  const crewOf = (t) => [{ user_id: t.created_by, name: t.created_by_name || "Creator" }, ...(t.participants || [])];

  const select = (t) => {
    const post = t.post_task || {};
    setSelId(t.id);
    setForm({
      name: t.name || "", start: t.start, end: t.end,
      brief_min: t.brief_min || 0, debrief_min: t.debrief_min || 0, remarks: t.remarks || "",
      primary_location_id: t.primary_location_id || "", additional_location_ids: t.additional_location_ids || [],
    });
    setPerfModes(post.modes_performed || t.mode_ids || []);
    setPerfSeqs(post.sequences_performed || t.sequence_ids || []);
    setAlloc(post.allocations || {});
  };

  useEffect(() => {
    if (selId && tasks.length && !form) {
      const t = tasks.find((x) => x.id === selId);
      if (t) select(t);
    }
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const completedAt = sel?.post_task?.completed_at;
  const editable = !sel || sel.status !== "completed" || sel.post_unlocked
    || (completedAt && new Date(completedAt).getTime() + editDays * 86400000 >= Date.now());

  const modeName = (id) => cfg.modes.find((m) => m.id === id)?.name || id;
  const seqName = (id) => cfg.sequences.find((s) => s.id === id)?.name || id;
  const setAllocVal = (item, uid, v) => setAlloc((a) => ({ ...a, [item]: { ...(a[item] || {}), [uid]: Math.max(0, Number(v) || 0) } }));
  const toggle = (setter) => (id) => setter((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${sel.id}/post-task`, {
        name: form.name || null, start: form.start, end: form.end,
        brief_min: Number(form.brief_min) || 0, debrief_min: Number(form.debrief_min) || 0,
        remarks: form.remarks || null,
        primary_location_id: form.primary_location_id || "", additional_location_ids: form.additional_location_ids,
        modes_performed: perfModes, sequences_performed: perfSeqs, allocations: alloc,
      });
      toast.success(sel.status === "completed" ? "Post-task updated" : `${taskLabel} marked completed`);
      setForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const unlock = async () => {
    try {
      await api.post(`/tasks/${sel.id}/unlock-post`);
      toast.success("Post-task unlocked");
      setForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const performedItems = [
    ...perfModes.map((id) => ({ id, name: modeName(id), cls: "text-amber-300" })),
    ...perfSeqs.map((id) => ({ id, name: seqName(id), cls: "text-blue-300" })),
  ];

  return (
    <div className="max-w-5xl" data-testid="post-task-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Operations</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Post {taskLabel}</h1>
        <p className="text-slate-400 mt-1 text-sm">
          Select a {taskLabel.toLowerCase()}, verify or edit its details, record which modes/sequences were actually performed and by whom, then mark it Completed.
        </p>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 mb-5">
        <div className="mb-4">
          <DateField label="Date" value={date} onChange={(v) => { setDate(v); setSelId(""); setForm(null); }} testId="post-task-date" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400 mb-4">
          <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={showCompleted}
            data-testid="post-task-show-completed" onChange={(e) => { setShowCompleted(e.target.checked); setSelId(""); setForm(null); }} />
          Also show completed {(user?.terminology?.task_label_plural || "Tasks").toLowerCase()} still inside the edit window
        </label>
        {eligible.length === 0 ? (
          <div className="text-sm text-slate-500" data-testid="post-task-empty">Nothing ready to post-{taskLabel.toLowerCase()} on this date — tasks appear once Committed (and Authorised, where the type requires it).</div>
        ) : (
          <div className="grid gap-2" data-testid="post-task-list">
            {eligible.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.scheduled;
              return (
                <button key={t.id} onClick={() => select(t)} data-testid={`post-task-select-${t.id}`}
                  className={`text-left px-4 py-3 rounded-lg border transition-colors ${selId === t.id ? "border-blue-500 bg-blue-600/10" : "border-slate-800 bg-slate-950/40 hover:border-slate-600"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{t.name || t.task_type_name}</span>
                    <span className="text-xs text-slate-400 font-mono-num">{t.start}–{t.end}</span>
                    <span className={`px-2 py-0.5 rounded border text-[10px] ${meta.cls}`}>{meta.label}</span>
                    {t.post_task?.completed_at && <span className="text-[10px] text-emerald-400">post-tasked by {t.post_task.completed_by_name}</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    by {t.created_by_name}{(t.participants || []).length ? ` · with ${t.participants.map((p) => p.name).join(", ")}` : ""}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {sel && form && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="post-task-detail">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="font-heading text-lg font-semibold">{sel.name || sel.task_type_name} <span className="text-slate-500 text-sm font-normal">· {sel.date}</span></div>
            {sel.status === "completed" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-600/15 border border-blue-600/40 text-blue-300 text-xs" data-testid="post-task-completed-banner">
                <Check className="h-3.5 w-3.5" /> Completed{sel.post_task?.completed_by_name ? ` by ${sel.post_task.completed_by_name}` : ""}
              </span>
            )}
          </div>

          {!editable && (
            <div className="mb-4 p-3 rounded-md border border-amber-600/40 bg-amber-600/10 flex items-center justify-between gap-3 flex-wrap" data-testid="post-task-locked">
              <div className="text-xs text-amber-300 inline-flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> The {editDays}-day post-task edit window has closed.</div>
              {privileged && (
                <button onClick={unlock} data-testid="post-task-unlock"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600/20 hover:bg-amber-600/30 border border-amber-600/50 text-amber-200 text-xs font-medium transition-colors">
                  <Unlock className="h-3.5 w-3.5" /> Unlock (supervisor)
                </button>
              )}
            </div>
          )}

          <fieldset disabled={!editable} className="space-y-4 disabled:opacity-60">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="post-task-name" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Start</label>
                <input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="post-task-start" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>End</label>
                <input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="post-task-end" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Brief (min)</label>
                <input type="number" min="0" value={form.brief_min} onChange={(e) => setForm({ ...form, brief_min: e.target.value })} data-testid="post-task-brief" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Debrief (min)</label>
                <input type="number" min="0" value={form.debrief_min} onChange={(e) => setForm({ ...form, debrief_min: e.target.value })} data-testid="post-task-debrief" className={inputCls} />
              </div>
            </div>

            <div>
              <label className={labelCls}>Remarks</label>
              <input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="post-task-remarks" className={inputCls} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Primary location</label>
                <select value={form.primary_location_id} onChange={(e) => setForm({ ...form, primary_location_id: e.target.value })} data-testid="post-task-primary-location" className={inputCls}>
                  <option value="">None</option>
                  {taskLocs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type ? ` — ${l.type}` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Additional locations</label>
                <div className="max-h-28 overflow-y-auto rounded-md border border-slate-800 bg-slate-950" data-testid="post-task-addlocs">
                  {taskLocs.filter((l) => l.id !== form.primary_location_id).map((l) => (
                    <label key={l.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-900 cursor-pointer" data-testid={`post-task-addloc-${l.id}`}>
                      <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={form.additional_location_ids.includes(l.id)}
                        onChange={() => setForm((f) => ({ ...f, additional_location_ids: f.additional_location_ids.includes(l.id) ? f.additional_location_ids.filter((x) => x !== l.id) : [...f.additional_location_ids, l.id] }))} />
                      {l.name}{l.type ? ` — ${l.type}` : ""}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className={labelCls}>Crew (cannot be changed here)</div>
              <div className="flex flex-wrap gap-1.5" data-testid="post-task-crew">
                {crewOf(sel).map((p) => (
                  <span key={p.user_id} className="px-2.5 py-1 rounded-md bg-slate-800 text-slate-200 text-xs border border-slate-700">
                    {p.name}{p.user_id === sel.created_by ? " (lead)" : ""}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <div className={labelCls}>Modes performed</div>
              {(sel.mode_ids || []).length === 0 && <div className="text-xs text-slate-500">No modes on the original {taskLabel.toLowerCase()}.</div>}
              <div className="flex flex-wrap gap-2" data-testid="post-task-modes">
                {(sel.mode_ids || []).map((id) => (
                  <label key={id} className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-xs cursor-pointer transition-colors ${perfModes.includes(id) ? "bg-amber-600/15 border-amber-600/40 text-amber-300" : "bg-slate-950 border-slate-800 text-slate-400"}`}
                    data-testid={`post-task-mode-${id}`}>
                    <input type="checkbox" className="accent-amber-600 h-3.5 w-3.5" checked={perfModes.includes(id)} onChange={() => toggle(setPerfModes)(id)} />
                    {modeName(id)}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className={labelCls}>Sequences performed</div>
              {(sel.sequence_ids || []).length === 0 && <div className="text-xs text-slate-500">No sequences on the original {taskLabel.toLowerCase()}.</div>}
              <div className="flex flex-wrap gap-2" data-testid="post-task-seqs">
                {(sel.sequence_ids || []).map((id) => (
                  <label key={id} className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-xs cursor-pointer transition-colors ${perfSeqs.includes(id) ? "bg-blue-600/15 border-blue-600/40 text-blue-300" : "bg-slate-950 border-slate-800 text-slate-400"}`}
                    data-testid={`post-task-seq-${id}`}>
                    <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={perfSeqs.includes(id)} onChange={() => toggle(setPerfSeqs)(id)} />
                    {seqName(id)}
                  </label>
                ))}
              </div>
            </div>

            {performedItems.length > 0 && (
              <div className="space-y-2" data-testid="post-task-allocations">
                <div className={labelCls}>Performed by — count per member</div>
                {performedItems.map((item) => (
                  <div key={item.id} className="p-3 rounded-md border border-slate-800 bg-slate-950/40" data-testid={`post-task-alloc-${item.id}`}>
                    <div className={`text-xs font-medium mb-2 ${item.cls}`}>{item.name}</div>
                    <div className="flex flex-wrap gap-3">
                      {crewOf(sel).map((p) => (
                        <label key={p.user_id} className="flex items-center gap-1.5 text-xs text-slate-300">
                          <span className="text-slate-400">{p.name}</span>
                          <input type="number" min="0" value={(alloc[item.id] || {})[p.user_id] ?? 0}
                            onChange={(e) => setAllocVal(item.id, p.user_id, e.target.value)}
                            data-testid={`post-task-alloc-${item.id}-${p.user_id}`}
                            className="w-16 px-2 py-1 rounded bg-slate-950 border border-slate-800 text-xs text-right" />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </fieldset>

          <div className="mt-5">
            <button onClick={submit} disabled={!editable || busy} data-testid="post-task-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50 transition-colors">
              <Check className="h-4 w-4" /> {busy ? "Saving…" : sel.status === "completed" ? "Save post-task changes" : "Verify & mark Completed"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
